import { Container, Grid } from "@mui/material";
import { endOfDay } from "date-fns";
import { FC, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  HistoryFilter,
  HistoryFilterDateTypes,
  ServiceHistoryFilterStorageKey,
} from "models/HistoryFilter";
import { useAppDispatch, useAppSelector } from "store";
import {
  resetServiceHistoryFilter,
  selectServiceHistoryFilter,
  setServiceHistoryFilter,
} from "store/slices/history.store";

import { AsolviDatePicker } from "components/AsolviDatePicker";
import { DateFilterType, FilterDateSelect } from "components/FilterDateSelect";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";

type HistoryFilterDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const ServiceHistoryFilterDialog: FC<HistoryFilterDialogProps> = (props) => {
  const { open, setOpen } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const filter = useAppSelector(selectServiceHistoryFilter);

  const dateOptions: DateFilterType[] = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "filter.last7days" }),
        value: "week",
      },
      {
        label: intl.formatMessage({ id: "filter.lastMonth" }),
        value: "1month",
      },
      {
        label: intl.formatMessage({ id: "filter.last12months" }),
        value: "12month",
      },
      {
        label: intl.formatMessage({ id: "filter.last24months" }),
        value: "24month",
      },
      {
        label: intl.formatMessage({ id: "filter.last5years" }),
        value: "5year",
      },
      {
        label: intl.formatMessage({ id: "filter.specificDate" }),
        value: "specificDate",
      },
    ],
    [intl]
  );

  const setFilter = useCallback(
    (filter: HistoryFilter) => {
      dispatch(setServiceHistoryFilter({ serviceHistoryFilter: filter }));
    },
    [dispatch]
  );

  const resetFilter = useCallback(() => {
    dispatch(resetServiceHistoryFilter());
  }, [dispatch]);

  const saveFilter = useCallback(() => {
    localStorage.setItem(ServiceHistoryFilterStorageKey, JSON.stringify(filter));
  }, [filter]);

  const setDate = useCallback(
    (type: HistoryFilterDateTypes) => {
      var specificDate = null;
      if (type === "specificDate") specificDate = filter.specificDate;
      setFilter({
        ...filter,
        typeDate: type,
        specificDate: specificDate,
      });
    },
    [filter, setFilter]
  );
  const setSpecificDate = useCallback(
    (value: Date | null) => {
      setFilter({
        ...filter,
        specificDate: value ? endOfDay(value) : null,
        typeDate: "specificDate",
      });
    },
    [filter, setFilter]
  );

  return (
    <FullScreenDialog
      title={intl.formatMessage({ id: "filter.setFilters" })}
      open={open}
      setOpen={setOpen}
      onClose={saveFilter}
      child={
        <Container sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FilterDateSelect value={filter.typeDate} options={dateOptions} onChange={setDate} />
            </Grid>
            {filter.typeDate === "specificDate" && (
              <Grid item xs={12}>
                <AsolviDatePicker
                  data-testid="ServiceHistoryFilterDialog-DatePickerFilter"
                  onChange={(value) => {
                    setSpecificDate(value);
                  }}
                  value={filter.specificDate}
                  label={intl.formatMessage({ id: "filter.specificDate" })}
                  clearable
                />
              </Grid>
            )}
            <Grid item xs={12} mt={2}>
              <SecondaryButton
                onClick={resetFilter}
                data-testid="ServiceHistoryFilterDialog-ResetButton"
              >
                <FormattedMessage id="filter.reset" />
              </SecondaryButton>
            </Grid>
            <Grid item xs={12}>
              <PrimaryButton
                data-testid="ServiceHistoryFilterDialog-SubmitButton"
                onClick={() => {
                  saveFilter();
                  setOpen(false);
                }}
              >
                <FormattedMessage id={"history.viewServiceHistory"} />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
